import { AccessDeniedScreen } from '@slims-pro/ui/organisms';
import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { useMe } from '@/hooks/use-me';
import { MODULE_PAGES } from '@/generated/modules';
import { useMeta } from '@/hooks/use-meta';
import { homeTarget } from '@/modules/home';
import { EXT_ROUTES } from '@/modules/ext-routes';
import { MODULE_ROUTES, type ModuleId } from '@/modules/registry';
import { ProShell } from '@/routes/pro-shell';
import { ProtectedRoute } from '@/routes/protected-route';
import { RequireExtension } from '@/routes/require-extension';
import { RequireModule } from '@/routes/require-module';
import { RootLayout } from '@/routes/root-layout';
import { RouteError } from '@/routes/route-error';

/** `/` → dashboard of `meta.defaultMode`, or the first enabled and permitted module. */
function HomeRedirect() {
	const meta = useMeta().data!;
	const permitted = new Set(useMe().data!.permissions.map((p) => p.module));
	const target = homeTarget(meta.defaultMode, meta.modules, permitted, (id) => id in MODULE_PAGES);
	if (!target) return <AccessDeniedScreen actionLabel="Ricarica" actionHref="/" />;
	return <Navigate to={`/${target}`} replace />;
}

// Only the pages this build contains (ADR-017): a module the client has off has no route.
const moduleIds = (Object.keys(MODULE_ROUTES) as ModuleId[]).filter((id) => MODULE_PAGES[id]);

// `handle.title` feeds the browser tab title (`useDocumentTitle`).
export const router = createBrowserRouter([
	{
		element: <RootLayout />,
		errorElement: <RouteError />,
		children: [
			{
				element: <ProtectedRoute />,
				children: [
					{
						element: <ProShell />,
						children: [
							{ index: true, element: <HomeRedirect /> },
							...moduleIds.map((id) => {
								const { path, title, feature } = MODULE_ROUTES[id];
								// Lazy: the chunk loads only once the module is enabled and permitted (ADR-008).
								const Page = MODULE_PAGES[id]!;
								return {
									path,
									element: (
										<RequireModule id={id} feature={feature}>
											<Suspense fallback={null}>
												<Page />
											</Suspense>
										</RequireModule>
									),
									handle: { title },
								};
							}),
							// Client extension routes (ADR-013 §4); pages may be lazy.
							...EXT_ROUTES.map(({ path, title, feature, module, Page }) => ({
								path: `x/${path}`,
								element: (
									<RequireExtension feature={feature} module={module}>
										<Suspense fallback={null}>
											<Page />
										</Suspense>
									</RequireExtension>
								),
								handle: { title },
							})),
							{ path: '*', element: <Navigate to="/" replace /> },
						],
					},
				],
			},
		],
	},
]);
